import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { format, subMonths, startOfMonth, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";

export interface MonthlyDREData {
  month: string;
  monthLabel: string;
  netRevenue: number;
  cmv: number;
  cmo: number;
  grossProfit: number;
  operatingExpenses: number;
  operatingResult: number;
  netProfit: number;
}

export interface CMODetail {
  category: string;
  amount: number;
  percentage: number;
}

export interface CMVDetail {
  category: string;
  amount: number;
  percentage: number;
}

export interface ExpenseComposition {
  category: string;
  amount: number;
  percentage: number;
}

export interface DREChartsData {
  monthlyData: MonthlyDREData[];
  cmoDetail: CMODetail[];
  cmvDetail: CMVDetail[];
  expenseComposition: ExpenseComposition[];
  cmoTotal: number;
  cmvTotal: number;
  expenseTotal: number;
}

interface UseDREMonthlyDataParams {
  months?: number;
  dateFrom?: string;
  dateTo?: string;
}

export function useDREMonthlyData({ months = 6, dateFrom, dateTo }: UseDREMonthlyDataParams = {}) {
  const { activeRestaurant } = useRestaurantContext();

  return useQuery({
    queryKey: ["dre-monthly-data", activeRestaurant?.id, months, dateFrom, dateTo],
    queryFn: async (): Promise<DREChartsData> => {
      if (!activeRestaurant) throw new Error("No restaurant");

      const today = new Date();
      const startDate = dateFrom || format(startOfMonth(subMonths(today, months - 1)), "yyyy-MM-dd");
      const endDate = dateTo || format(endOfMonth(today), "yyyy-MM-dd");

      // Fetch all required data
      const [ordersRes, expensesRes, categoriesRes, stockRes] = await Promise.all([
        supabase
          .from("orders")
          .select("id, subtotal, discount, additional_fees, delivery_fee, total, status, created_at")
          .eq("restaurant_id", activeRestaurant.id)
          .gte("created_at", `${startDate}T00:00:00`)
          .lte("created_at", `${endDate}T23:59:59`),
        supabase
          .from("expenses" as any)
          .select("id, amount, status, category_id, expense_date")
          .eq("restaurant_id", activeRestaurant.id)
          .gte("expense_date", startDate)
          .lte("expense_date", endDate)
          .in("status", ["open", "paid"]),
        supabase
          .from("expense_categories" as any)
          .select("id, name")
          .eq("restaurant_id", activeRestaurant.id),
        supabase
          .from("stock_movements")
          .select("id, quantity, unit_cost, type, created_at")
          .eq("restaurant_id", activeRestaurant.id)
          .eq("type", "sale")
          .gte("created_at", `${startDate}T00:00:00`)
          .lte("created_at", `${endDate}T23:59:59`),
      ]);

      const orders = ordersRes.data || [];
      const expenses = (expensesRes.data as any[]) || [];
      const categories = (categoriesRes.data as any[]) || [];
      const stockMovements = stockRes.data || [];

      const categoryMap = new Map(categories.map((c: any) => [c.id, c.name]));

      // Keywords for CMO classification (personnel/labor)
      const cmoKeywords = ["pessoal", "salário", "salario", "funcionário", "funcionario", "freelancer", "encargo", "inss", "fgts", "férias", "ferias", "13º", "benefício", "beneficio", "vale", "convênio", "convenio"];
      
      // Keywords for CMV classification (materials/products)
      const cmvKeywords = ["matéria", "materia", "ingrediente", "produto", "revenda", "embalagem", "insumo", "mercadoria", "compra", "estoque"];

      // Classify expenses
      const classifyExpense = (categoryName: string): "cmo" | "cmv" | "other" => {
        const lowerName = categoryName.toLowerCase();
        if (cmoKeywords.some(k => lowerName.includes(k))) return "cmo";
        if (cmvKeywords.some(k => lowerName.includes(k))) return "cmv";
        return "other";
      };

      // Build monthly data
      const monthlyMap = new Map<string, MonthlyDREData>();
      
      // Initialize months
      for (let i = 0; i < months; i++) {
        const monthDate = subMonths(today, months - 1 - i);
        const monthKey = format(monthDate, "yyyy-MM");
        const monthLabel = format(monthDate, "MMM/yy", { locale: ptBR });
        monthlyMap.set(monthKey, {
          month: monthKey,
          monthLabel,
          netRevenue: 0,
          cmv: 0,
          cmo: 0,
          grossProfit: 0,
          operatingExpenses: 0,
          operatingResult: 0,
          netProfit: 0,
        });
      }

      // Process orders for revenue
      orders.forEach((order) => {
        if (order.status !== "delivered") return;
        const monthKey = format(new Date(order.created_at), "yyyy-MM");
        const data = monthlyMap.get(monthKey);
        if (data) {
          const revenue = Number(order.subtotal || 0) + Number(order.additional_fees || 0) + Number(order.delivery_fee || 0);
          const discount = Number(order.discount || 0);
          data.netRevenue += revenue - discount;
        }
      });

      // Process stock movements for CMV
      stockMovements.forEach((mov) => {
        const monthKey = format(new Date(mov.created_at), "yyyy-MM");
        const data = monthlyMap.get(monthKey);
        if (data) {
          data.cmv += Math.abs(Number(mov.quantity || 0)) * Number(mov.unit_cost || 0);
        }
      });

      // Expense composition aggregation
      const expenseByCategory = new Map<string, number>();
      const cmoByCategory = new Map<string, number>();
      let cmoTotal = 0;
      let cmvFromExpenses = 0;
      const cmvByCategory = new Map<string, number>();

      // Process expenses
      expenses.forEach((exp: any) => {
        const monthKey = format(new Date(exp.expense_date), "yyyy-MM");
        const categoryName = categoryMap.get(exp.category_id) || "Outras";
        const amount = Number(exp.amount || 0);
        const classification = classifyExpense(categoryName);

        // Expense composition
        expenseByCategory.set(categoryName, (expenseByCategory.get(categoryName) || 0) + amount);

        // Monthly data
        const data = monthlyMap.get(monthKey);
        if (data) {
          data.operatingExpenses += amount;
          if (classification === "cmo") {
            data.cmo += amount;
          }
        }

        // CMO detail
        if (classification === "cmo") {
          cmoByCategory.set(categoryName, (cmoByCategory.get(categoryName) || 0) + amount);
          cmoTotal += amount;
        }

        // CMV from expenses (materials, products purchased)
        if (classification === "cmv") {
          cmvByCategory.set(categoryName, (cmvByCategory.get(categoryName) || 0) + amount);
          cmvFromExpenses += amount;
        }
      });

      // Calculate derived values for monthly data
      monthlyMap.forEach((data) => {
        data.grossProfit = data.netRevenue - data.cmv;
        data.operatingResult = data.grossProfit - data.operatingExpenses;
        data.netProfit = data.operatingResult; // Simplified, without financial expenses
      });

      // Build CMV detail from stock movements + expenses
      // Get CMV from stock movements (this is the main CMV)
      const cmvFromStock = stockMovements.reduce(
        (sum, m) => sum + Math.abs(Number(m.quantity || 0)) * Number(m.unit_cost || 0),
        0
      );

      const cmvDetail: CMVDetail[] = [];
      const totalCMV = cmvFromStock + cmvFromExpenses;

      if (cmvFromStock > 0) {
        cmvDetail.push({
          category: "Baixa de Estoque (Vendas)",
          amount: cmvFromStock,
          percentage: totalCMV > 0 ? (cmvFromStock / totalCMV) * 100 : 0,
        });
      }

      cmvByCategory.forEach((amount, category) => {
        cmvDetail.push({
          category,
          amount,
          percentage: totalCMV > 0 ? (amount / totalCMV) * 100 : 0,
        });
      });

      // Build CMO detail
      const cmoDetail: CMODetail[] = [];
      cmoByCategory.forEach((amount, category) => {
        cmoDetail.push({
          category,
          amount,
          percentage: cmoTotal > 0 ? (amount / cmoTotal) * 100 : 0,
        });
      });
      cmoDetail.sort((a, b) => b.amount - a.amount);

      // Build expense composition
      const expenseTotal = Array.from(expenseByCategory.values()).reduce((a, b) => a + b, 0);
      const expenseComposition: ExpenseComposition[] = [];
      expenseByCategory.forEach((amount, category) => {
        expenseComposition.push({
          category,
          amount,
          percentage: expenseTotal > 0 ? (amount / expenseTotal) * 100 : 0,
        });
      });
      expenseComposition.sort((a, b) => b.amount - a.amount);

      return {
        monthlyData: Array.from(monthlyMap.values()),
        cmoDetail,
        cmvDetail,
        expenseComposition,
        cmoTotal,
        cmvTotal: totalCMV,
        expenseTotal,
      };
    },
    enabled: !!activeRestaurant,
  });
}
